import React, { useState } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { useTeam } from '../../../context/TeamContext';
import { useShiftPlan } from '../../../context/ShiftPlanContext';
import { SHIFT_TYPES } from '../../../data/nurseAppModels';

const PlanSwapRequestModal = ({
  isOpen,
  onClose,
  planId,
  plan,
  date,
  userShifts = [],
  otherShifts = [],
  teamMembersWithProfile = []
}) => {
  const { currentUser } = useAuth();
  const { currentTeam } = useTeam();
  const { requestPlanSwap } = useShiftPlan();
  
  const [swapType, setSwapType] = useState(null); // 'SAME_DAY', 'DIFFERENT_DAY', 'COVER_REQUEST'
  const [selectedMember, setSelectedMember] = useState(null);
  const [selectedShift, setSelectedShift] = useState(null);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Get shift type label
  const getShiftTypeLabel = (type) => {
    switch (type) {
      case SHIFT_TYPES.MORNING: return 'เช้า';
      case SHIFT_TYPES.AFTERNOON: return 'บ่าย';
      case SHIFT_TYPES.NIGHT: return 'ดึก';
      default: return type;
    }
  };

  // Get shift type icon
  const getShiftTypeIcon = (type) => {
    switch (type) {
      case SHIFT_TYPES.MORNING: return '☀️';
      case SHIFT_TYPES.AFTERNOON: return '🌤️';
      case SHIFT_TYPES.NIGHT: return '🌙';
      default: return '';
    }
  };

  // Get shift type color
  const getShiftTypeColor = (type) => {
    switch (type) {
      case SHIFT_TYPES.MORNING: return 'bg-green-500';
      case SHIFT_TYPES.AFTERNOON: return 'bg-yellow-500';
      case SHIFT_TYPES.NIGHT: return 'bg-blue-500';
      default: return 'bg-gray-500';
    }
  };

  // Get user profile image
  const getUserProfileImage = (userId) => {
    const member = teamMembersWithProfile.find(m => m.uid === userId);
    return member?.photoURL || null;
  };

  // Get user display name
  const getUserDisplayName = (userId) => {
    const member = teamMembersWithProfile.find(m => m.uid === userId);
    return member?.displayName || member?.name || 'Unknown';
  };

  // Format date
  const formatDate = (dateObj) => {
    return dateObj.toLocaleDateString('th-TH', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Handle swap type selection
  const handleSwapTypeSelect = (type) => {
    setSwapType(type);
    setSelectedMember(null);
    setSelectedShift(null);
    setError(null);
  };

  // Handle member selection
  const handleMemberSelect = (memberId) => {
    setSelectedMember(memberId);
    setSelectedShift(null);
  };

  // Handle shift selection
  const handleShiftSelect = (shift) => {
    setSelectedShift(shift);
  };

  // Handle submit
  const handleSubmit = async () => {
    setError(null);
    
    if (!swapType) {
      setError('กรุณาเลือกประเภทการแลกเวร');
      return;
    }

    if (!selectedMember) {
      setError('กรุณาเลือกสมาชิกที่ต้องการแลกเวรด้วย');
      return;
    }

    if (swapType === 'DIFFERENT_DAY' && !selectedShift) {
      setError('กรุณาเลือกเวรที่ต้องการแลก');
      return;
    }

    setLoading(true);
    try {
      const dateStr = date.toISOString().split('T')[0];
      const userShift = userShifts[0]; // Get first user shift for this date
      
      let requestedShift = null;
      if (swapType === 'SAME_DAY') {
        // Find the selected member's shift for same day
        const memberShift = otherShifts.find(s => s.userId === selectedMember);
        if (memberShift) {
          requestedShift = {
            date: dateStr,
            shiftType: memberShift.shiftType,
            userId: selectedMember,
            userName: getUserDisplayName(selectedMember)
          };
        }
      } else if (swapType === 'DIFFERENT_DAY' && selectedShift) {
        requestedShift = {
          date: selectedShift.date,
          shiftType: selectedShift.shiftType,
          userId: selectedMember,
          userName: getUserDisplayName(selectedMember)
        };
      }

      const selectedMemberName = getUserDisplayName(selectedMember);
      
      const swapData = {
        swapType,
        date: dateStr,
        shiftType: userShift?.shiftType || SHIFT_TYPES.MORNING,
        requestedTo: selectedMember,
        requestedToName: selectedMemberName,
        requestedShift: requestedShift,
        message: message.trim()
      };

      const result = await requestPlanSwap(planId, swapData);

      if (result.success) {
        alert('ส่งคำขอแลกเวรเรียบร้อยแล้ว!');
        onClose();
      } else {
        setError(result.error || 'เกิดข้อผิดพลาดในการส่งคำขอ');
      }
    } catch (err) {
      setError(err.message || 'เกิดข้อผิดพลาดในการส่งคำขอ');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  // Get available members for same day swap
  const availableSameDayMembers = otherShifts
    .filter(shift => shift.userId !== currentUser?.uid)
    .map(shift => ({
      userId: shift.userId,
      userName: shift.userName || getUserDisplayName(shift.userId),
      shiftType: shift.shiftType,
      photoURL: getUserProfileImage(shift.userId)
    }));

  // Get unique members
  const uniqueMembers = availableSameDayMembers.reduce((acc, member) => {
    if (!acc.find(m => m.userId === member.userId)) {
      acc.push(member);
    }
    return acc;
  }, []);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-800">ขอแลกเวร</h2>
          <p className="text-sm text-gray-600 mt-1">
            วันที่ {formatDate(date)}
          </p>
        </div>

        <div className="p-6 space-y-6">
          {/* User's shifts */}
          {userShifts.length > 0 && (
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm font-medium text-blue-900 mb-2">เวรของคุณ:</p>
              <div className="flex flex-wrap gap-2">
                {userShifts.map((shift, idx) => (
                  <div
                    key={idx}
                    className={`${getShiftTypeColor(shift.shiftType)} text-white text-sm px-3 py-1.5 rounded flex items-center gap-2`}
                  >
                    <span>{getShiftTypeIcon(shift.shiftType)}</span>
                    <span>{getShiftTypeLabel(shift.shiftType)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Swap Type Selection */}
          {!swapType ? (
            <div>
              <p className="text-sm font-medium text-gray-700 mb-3">เลือกประเภทการแลกเวร:</p>
              <div className="space-y-2">
                {/* Same Day Swap */}
                {availableSameDayMembers.length > 0 && (
                  <button
                    onClick={() => handleSwapTypeSelect('SAME_DAY')}
                    className="w-full p-4 text-left border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                        <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                        </svg>
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-gray-800">แลกเวรในวันเดียวกัน</p>
                        <p className="text-xs text-gray-600">แลกกับเพื่อนที่ลงเวรวันเดียวกัน</p>
                      </div>
                    </div>
                  </button>
                )}

                {/* Different Day Swap */}
                <button
                  onClick={() => handleSwapTypeSelect('DIFFERENT_DAY')}
                  className="w-full p-4 text-left border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                      <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-gray-800">แลกเวรต่างวัน</p>
                      <p className="text-xs text-gray-600">แลกกับเพื่อนที่ลงเวรวันอื่น</p>
                    </div>
                  </div>
                </button>

                {/* Cover Request */}
                {userShifts.length > 0 && (
                  <button
                    onClick={() => handleSwapTypeSelect('COVER_REQUEST')}
                    className="w-full p-4 text-left border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                        <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-gray-800">ขอให้ขึ้นเวรแทน</p>
                        <p className="text-xs text-gray-600">ขอให้เพื่อนขึ้นเวรแทน (ไม่ต้องแลก)</p>
                      </div>
                    </div>
                  </button>
                )}
              </div>
            </div>
          ) : (
            <>
              {/* Back button */}
              <button
                onClick={() => {
                  setSwapType(null);
                  setSelectedMember(null);
                  setSelectedShift(null);
                }}
                className="text-blue-600 hover:text-blue-700 flex items-center gap-2 text-sm"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                กลับ
              </button>

              {/* Same Day Swap */}
              {swapType === 'SAME_DAY' && (
                <div className="space-y-4">
                  <p className="text-sm font-medium text-gray-700">เลือกเพื่อนที่ต้องการแลก:</p>
                  <div className="space-y-2">
                    {uniqueMembers.map((member) => (
                      <button
                        key={member.userId}
                        onClick={() => handleMemberSelect(member.userId)}
                        className={`w-full p-3 rounded-lg border-2 transition ${
                          selectedMember === member.userId
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          {member.photoURL ? (
                            <img
                              src={member.photoURL}
                              alt={member.userName}
                              className="w-10 h-10 rounded-full object-cover"
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center text-white font-semibold">
                              {member.userName?.charAt(0) || '?'}
                            </div>
                          )}
                          <div className="flex-1 text-left">
                            <p className="font-medium text-gray-800">{member.userName}</p>
                            <p className="text-xs text-gray-600">
                              {getShiftTypeIcon(member.shiftType)} {getShiftTypeLabel(member.shiftType)}
                            </p>
                          </div>
                          {selectedMember === member.userId && (
                            <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Different Day Swap */}
              {swapType === 'DIFFERENT_DAY' && (
                <div className="space-y-4">
                  <p className="text-sm text-gray-600">
                    ฟีเจอร์นี้จะพัฒนาต่อไป - สามารถเลือกวันที่และเพื่อนที่ต้องการแลกได้
                  </p>
                </div>
              )}

              {/* Cover Request */}
              {swapType === 'COVER_REQUEST' && (
                <div className="space-y-4">
                  <p className="text-sm font-medium text-gray-700">เลือกเพื่อนที่ต้องการให้ขึ้นเวรแทน:</p>
                  <div className="space-y-2">
                    {teamMembersWithProfile
                      .filter(m => m.uid !== currentUser?.uid)
                      .map((member) => (
                        <button
                          key={member.uid}
                          onClick={() => handleMemberSelect(member.uid)}
                          className={`w-full p-3 rounded-lg border-2 transition ${
                            selectedMember === member.uid
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            {member.photoURL ? (
                              <img
                                src={member.photoURL}
                                alt={member.displayName}
                                className="w-10 h-10 rounded-full object-cover"
                              />
                            ) : (
                              <div className="w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center text-white font-semibold">
                                {member.displayName?.charAt(0) || '?'}
                              </div>
                            )}
                            <div className="flex-1 text-left">
                              <p className="font-medium text-gray-800">{member.displayName}</p>
                            </div>
                            {selectedMember === member.uid && (
                              <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </div>
                        </button>
                      ))}
                  </div>
                </div>
              )}

              {/* Message */}
              {selectedMember && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    ข้อความ/เหตุผล <span className="text-gray-500">(ไม่บังคับ)</span>
                  </label>
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    rows="3"
                    placeholder="ระบุเหตุผลหรือข้อความถึงสมาชิก..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              )}

              {/* Error */}
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-600 text-sm">{error}</p>
                </div>
              )}

              {/* Submit Button */}
              {selectedMember && (
                <div className="flex gap-3">
                  <button
                    onClick={onClose}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
                  >
                    ยกเลิก
                  </button>
                  <button
                    onClick={handleSubmit}
                    disabled={loading}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? 'กำลังส่ง...' : 'ส่งคำขอ'}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PlanSwapRequestModal;

